import React, {
  VoidFunctionComponent,
  ComponentProps,
  useMemo,
  useState,
  CSSProperties
} from 'react';
import { HtmlVector } from 'record-it-utils/misc/HtmlVector/HtmlVector';
import { useElementSize } from '../../hooks/useElementSize/useElementSize';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import styles from './ClockPicker.module.css';
import {
  TickConfiguration,
  useCalculatedTickConfiguration
} from './useCalculatedTickConfiguration';
import { useEventHandlers } from './useEventHandlers';

export type ClockPickerProps = PropsWithoutChildren<ComponentProps<'div'>> & {
  value: number;
  tickConfiguration: TickConfiguration;
  /**
   * gets called only if a new value got selected
   */
  onValueChanged?: (value: number) => void;
  /**
   * gets called everytime a value got selected
   */
  onValueSelected?: (value: number) => void;
};

export const ClockPicker: VoidFunctionComponent<ClockPickerProps> = React.memo(
  (props) => {
    const {
      tickConfiguration,
      value,
      onValueChanged,
      onValueSelected,
      className,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const containerSize = useElementSize(container);

    const calculatedTicks = useCalculatedTickConfiguration({
      containerSize,
      tickConfiguration
    });

    const { highlightedTick, containerProps } = useEventHandlers({
      value,
      onValueChanged,
      onValueSelected,
      container,
      allTicks: calculatedTicks.allTicks
    });

    const highlightedTickCircleConnectorStyle =
      useMemo<CSSProperties | null>(() => {
        if (!containerSize || !highlightedTick) {
          return null;
        }

        const vector = highlightedTick.position.substractVector(
          new HtmlVector(
            containerSize.clientWidth / 2,
            containerSize.clientWidth / 2
          )
        );

        return {
          width: `${vector.getLength()}px`,
          transform: `rotate(${(vector.getAngle() || 0) * -1}deg) translate(0, -50%)`
        };
      }, [containerSize, highlightedTick]);

    const renderTick = (
      tick: CalculatedTick,
      testId: string,
      extraClass: string
    ): JSX.Element => {
      return (
        <div
          key={tick.displayedValue}
          className={classes(
            styles.tick,
            extraClass,
            tick === highlightedTick ? styles.tickHighlighted : null
          )}
          style={{
            left: `${tick.position.getX()}px`,
            top: `${tick.position.getY()}px`
          }}
          data-testid={testId}
        >
          {tick.displayedValue}
        </div>
      );
    };

    return (
      <div
        {...remainingProps}
        {...containerProps}
        className={classes(className, styles.container)}
        ref={setContainer}
      >
        <div className={styles.centerPoint}></div>
        {highlightedTickCircleConnectorStyle ? (
          <div
            className={styles.highlightedTickCircleConnector}
            style={highlightedTickCircleConnectorStyle}
          ></div>
        ) : null}
        {highlightedTick ? (
          <div
            className={styles.highlightedTickCircle}
            style={{
              left: `${highlightedTick.position.getX()}px`,
              top: `${highlightedTick.position.getY()}px`
            }}
            data-testid="ClockPicker.HighlightCircle"
          ></div>
        ) : null}
        {calculatedTicks.outerTicks
          .filter((t) => t.selectable)
          .map((tick) =>
            renderTick(tick, 'ClockPicker.OuterTick', styles.outerTick)
          )}
        {calculatedTicks.innerTicks
          .filter((t) => t.selectable)
          .map((tick) =>
            renderTick(tick, 'ClockPicker.InnerTick', styles.innerTick)
          )}
      </div>
    );
  }
);

type CalculatedTick = {
  value: number;
  displayedValue: string;
  position: HtmlVector;
};
